import React, { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentUser, signOut, fetchUserAttributes } from 'aws-amplify/auth';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

interface AuthContextType {
  isAuthenticated: boolean;
  user: { username: string; email: string; name?: string; givenName?: string; familyName?: string } | null;
  loading: boolean;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<{ username: string; email: string; name?: string; givenName?: string; familyName?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      // In mock mode, automatically authenticate with mock user
      if (USE_MOCK) {
        const mockUser = {
          username: 'admin@example.com',
          email: 'admin@example.com',
          givenName: 'Admin',
          familyName: 'User',
          name: 'Admin User'
        };
        setUser(mockUser);
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }

      const currentUser = await getCurrentUser();
      // Fetch user attributes to get first name, last name, etc.
      const userAttributes = await fetchUserAttributes();
      
      const userData = {
        username: currentUser.username,
        email: userAttributes.email || currentUser.signInDetails?.loginId || 'unknown@example.com',
        givenName: userAttributes.given_name,
        familyName: userAttributes.family_name,
        name: userAttributes.name || (userAttributes.given_name && userAttributes.family_name 
          ? `${userAttributes.given_name} ${userAttributes.family_name}` 
          : userAttributes.email || currentUser.username)
      };
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.log('User not authenticated:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      // In mock mode, just clear the user state
      if (USE_MOCK) {
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      await signOut();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    signOut: handleSignOut,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};